import * as React from "react";
import {
  Play, Loader2, CheckCircle, XCircle, Clock, Settings,
  Zap, History, ToggleLeft, ChevronDown, ChevronRight, Activity, BarChart3,
  Mail, Save, Send
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  success: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  error: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
  started: { icon: Loader2, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  running: { icon: Loader2, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
};

const JOB_ICONS = {
  anomaly_baseline: "bg-orange-100 text-orange-700",
  anomaly_detection: "bg-red-100 text-red-700",
  percentile_baseline: "bg-cyan-100 text-cyan-700",
  percentile_analysis: "bg-blue-100 text-blue-700",
  evaluate_alerts: "bg-amber-100 text-amber-700",
  daily_digest: "bg-indigo-100 text-indigo-700",
};

const MODULE_GROUPS = [
  {
    id: "anomaly_v2",
    title: "Anomalias v2",
    description: "Z-Score adaptativo con umbrales por variabilidad",
    icon: Activity,
    color: "from-orange-500 to-red-500",
    jobs: ["anomaly_baseline", "anomaly_detection"],
  },
  {
    id: "percentile_v2",
    title: "Percentiles v2",
    description: "Percentiles P10-P90 historicos por dia de la semana",
    icon: BarChart3,
    color: "from-cyan-500 to-blue-500",
    jobs: ["percentile_baseline", "percentile_analysis"],
  },
  {
    id: "alerts",
    title: "Alertas de Performance",
    description: "Evaluacion automatica de reglas de alerta y notificaciones",
    icon: Zap,
    color: "from-amber-500 to-orange-500",
    jobs: ["evaluate_alerts"],
  },
  {
    id: "digest",
    title: "Digest Diario",
    description: "Resumen matutino con estado de todas las cuentas (sin gasto / bajo / ok)",
    icon: Mail,
    color: "from-indigo-500 to-purple-500",
    jobs: ["daily_digest"],
  },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({ value: String(i), label: String(i).padStart(2, "0") }));
const MINUTE_OPTIONS = [
  { value: "0", label: "00" }, { value: "5", label: "05" }, { value: "10", label: "10" },
  { value: "15", label: "15" }, { value: "20", label: "20" }, { value: "25", label: "25" },
  { value: "30", label: "30" }, { value: "35", label: "35" }, { value: "40", label: "40" },
  { value: "45", label: "45" }, { value: "50", label: "50" }, { value: "55", label: "55" },
];
const DOW_OPTIONS = [
  { value: "*", label: "Todos los dias" },
  { value: "mon", label: "Lunes" }, { value: "tue", label: "Martes" },
  { value: "wed", label: "Miercoles" }, { value: "thu", label: "Jueves" },
  { value: "fri", label: "Viernes" }, { value: "sat", label: "Sabado" },
  { value: "sun", label: "Domingo" },
  { value: "mon-fri", label: "Lun-Vie" },
];

export const DataPulseAdmin = ({ isAdmin = false }) => {
  const [schedules, setSchedules] = React.useState([]);
  const [logs, setLogs] = React.useState([]);
  const [logsTotal, setLogsTotal] = React.useState(0);
  const [logsHasMore, setLogsHasMore] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [triggeringJob, setTriggeringJob] = React.useState(null);
  const [savingJob, setSavingJob] = React.useState(null);
  const [logFilter, setLogFilter] = React.useState("all");
  const [alertRules, setAlertRules] = React.useState([]);
  const [expandedLogs, setExpandedLogs] = React.useState({});
  const [expandedGroups, setExpandedGroups] = React.useState({
    anomaly_v2: false,
    percentile_v2: false,
    alerts: false,
    digest: false,
  });
  const [digestSettings, setDigestSettings] = React.useState({ recipients: "", low_pct_threshold: -30, updated_by: null, updated_at: null });
  const [digestDraft, setDigestDraft] = React.useState({ recipients: "", low_pct_threshold: -30 });
  const [savingDigest, setSavingDigest] = React.useState(false);
  const [testingDigest, setTestingDigest] = React.useState(false);

  React.useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [schedRes, logsRes, alertRes, digestRes] = await Promise.all([
        axios.get(`${API}/admin/datapulse/schedules`, { withCredentials: true }),
        axios.get(`${API}/admin/datapulse/logs?limit=30`, { withCredentials: true }),
        axios.get(`${API}/admin/datapulse/alert-rules-summary`, { withCredentials: true }).catch(() => ({ data: { rules: [] } })),
        axios.get(`${API}/admin/digest-settings`, { withCredentials: true }).catch(() => ({ data: { recipients: "", low_pct_threshold: -30 } })),
      ]);
      setSchedules(schedRes.data.schedules || []);
      setLogs(logsRes.data.logs || []);
      setLogsTotal(logsRes.data.total || 0);
      setLogsHasMore(logsRes.data.has_more || false);
      setAlertRules(alertRes.data.rules || []);
      const ds = digestRes.data || {};
      setDigestSettings(ds);
      setDigestDraft({ recipients: ds.recipients || "", low_pct_threshold: ds.low_pct_threshold ?? -30 });
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const handleSaveDigest = async () => {
    if (!isAdmin) return;
    setSavingDigest(true);
    try {
      await axios.put(`${API}/admin/digest-settings`, digestDraft, { withCredentials: true });
      toast.success("Configuracion de digest guardada");
      const res = await axios.get(`${API}/admin/digest-settings`, { withCredentials: true });
      setDigestSettings(res.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingDigest(false);
    }
  };

  const handleTestDigest = async () => {
    if (!isAdmin) return;
    setTestingDigest(true);
    try {
      const res = await axios.post(`${API}/admin/digest-settings/test-send`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.sent_to) {
        toast.success(`Digest enviado a ${d.test_recipient}`, {
          description: `${d.total_accounts} cuentas · ${d.zero_spend} sin gasto · ${d.low} bajo · ${d.ok} ok`,
          duration: 6000,
        });
      } else if (d.status === "skipped") {
        toast.warning(d.message || "Digest no enviado", { duration: 5000 });
      } else {
        toast.info(d.message || "Digest ejecutado", { duration: 5000 });
      }
      // Reload logs to show the test execution
      setTimeout(loadData, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar digest de prueba");
    } finally {
      setTestingDigest(false);
    }
  };

  const loadMoreLogs = async () => {
    setLoadingMore(true);
    try {
      const skip = logs.length;
      const filter = logFilter !== "all" ? `&job_id=${logFilter}` : "";
      const res = await axios.get(`${API}/admin/datapulse/logs?limit=30&skip=${skip}${filter}`, { withCredentials: true });
      setLogs(prev => [...prev, ...(res.data.logs || [])]);
      setLogsHasMore(res.data.has_more || false);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  const filterLogs = async (jobId) => {
    setLogFilter(jobId);
    try {
      const filter = jobId !== "all" ? `&job_id=${jobId}` : "";
      const res = await axios.get(`${API}/admin/datapulse/logs?limit=30${filter}`, { withCredentials: true });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsHasMore(res.data.has_more || false);
    } catch (e) { console.error(e); }
  };

  const handleToggle = async (jobId, enabled) => {
    if (!isAdmin) return;
    setSavingJob(jobId);
    try {
      await axios.put(`${API}/admin/datapulse/schedules/${jobId}`, { enabled }, { withCredentials: true });
      setSchedules(prev => prev.map(s => s.job_id === jobId ? { ...s, enabled } : s));
      toast.success(enabled ? "Job activado" : "Job desactivado");
    } catch (e) { toast.error("Error al actualizar"); }
    finally { setSavingJob(null); }
  };

  const handleUpdateSchedule = async (jobId, field, value) => {
    if (!isAdmin) return;
    setSavingJob(jobId);
    try {
      const update = { [field]: value };
      const sched = schedules.find(s => s.job_id === jobId);
      if (field === "cron_hour" || field === "cron_minute" || field === "cron_day_of_week") {
        const hour = field === "cron_hour" ? value : sched?.cron_hour || 0;
        const minute = field === "cron_minute" ? value : sched?.cron_minute || 0;
        const dow = field === "cron_day_of_week" ? value : sched?.cron_day_of_week || "*";
        const dowLabel = DOW_OPTIONS.find(o => o.value === dow)?.label || dow;
        update.frequency_label = `${dowLabel === "Todos los dias" ? "Diario" : dowLabel} (${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")})`;
      }
      await axios.put(`${API}/admin/datapulse/schedules/${jobId}`, update, { withCredentials: true });
      setSchedules(prev => prev.map(s => s.job_id === jobId ? { ...s, ...update } : s));
      toast.success("Configuracion actualizada");
    } catch (e) { toast.error("Error al actualizar"); }
    finally { setSavingJob(null); }
  };

  const handleTrigger = async (jobId) => {
    if (!isAdmin) return;
    setTriggeringJob(jobId);
    try {
      await axios.post(`${API}/admin/datapulse/trigger/${jobId}`, {}, { withCredentials: true });
      toast.info("Job iniciado manualmente");
      // Reload logs after a delay
      setTimeout(loadData, 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setTimeout(() => setTriggeringJob(null), 3000); }
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          DataPulse
        </h2>
        <p className="text-sm text-slate-500 mt-1">Automatizacion de pipelines de datos — ejecucion programada de jobs</p>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config" className="flex items-center gap-1.5">
            <Settings className="h-3.5 w-3.5" />Configuracion
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />Historial
            <Badge variant="outline" className="text-[10px] ml-1">{logsTotal}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-4 mt-4">
          {!isAdmin && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
              Solo los administradores pueden modificar la configuracion. Estas en modo lectura.
            </div>
          )}
          {MODULE_GROUPS.map(group => {
            const GroupIcon = group.icon;
            const groupJobs = schedules.filter(s => group.jobs.includes(s.job_id));
            const activeCount = groupJobs.filter(s => s.enabled).length;
            const isExpanded = expandedGroups[group.id] === true;

            return (
              <Card key={group.id} className={`overflow-hidden transition-all ${isExpanded ? "shadow-md" : "hover:shadow-sm"}`}>
                <div
                  className={`cursor-pointer select-none p-4 flex items-center justify-between transition-colors ${isExpanded ? "bg-slate-50/50" : "hover:bg-slate-50/50"}`}
                  onClick={() => setExpandedGroups(prev => ({ ...prev, [group.id]: !isExpanded }))}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${group.color}`}>
                      <GroupIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        {group.title}
                        <Badge variant="outline" className="text-[10px] font-normal">{activeCount}/{groupJobs.length} activos</Badge>
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">{group.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!isExpanded && (
                      <div className="hidden md:flex items-center gap-1.5">
                        {groupJobs.map(j => (
                          <span key={j.job_id} className={`inline-block w-2 h-2 rounded-full ${j.enabled ? "bg-emerald-400" : "bg-slate-300"}`} />
                        ))}
                      </div>
                    )}
                    <div className={`p-1 rounded-md transition-transform ${isExpanded ? "rotate-90" : ""}`}>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 space-y-2 border-t border-slate-100">
                    {groupJobs.map(s => (
                      <div key={s.job_id} className={`p-3 rounded-lg border transition-all ${!s.enabled ? "opacity-50 bg-slate-50 border-slate-100" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2.5 flex-1">
                            <div className={`p-1.5 rounded-md ${JOB_ICONS[s.job_id] || "bg-slate-100"}`}>
                              <Zap className="h-3.5 w-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h4 className="font-medium text-slate-800 text-xs">{s.name}</h4>
                                <Badge className={`text-[9px] ${s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                                  {s.enabled ? "Activo" : "Inactivo"}
                                </Badge>
                              </div>
                              <p className="text-[11px] text-slate-400 mb-2">{s.description}</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Clock className="h-3 w-3 text-slate-300" />
                                <Select value={String(s.cron_hour)} onValueChange={(v) => handleUpdateSchedule(s.job_id, "cron_hour", parseInt(v))} disabled={!isAdmin}>
                                  <SelectTrigger className="h-6 w-[55px] text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{HOUR_OPTIONS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <span className="text-[11px] text-slate-300">:</span>
                                <Select value={String(s.cron_minute || 0)} onValueChange={(v) => handleUpdateSchedule(s.job_id, "cron_minute", parseInt(v))} disabled={!isAdmin}>
                                  <SelectTrigger className="h-6 w-[55px] text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{MINUTE_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <Select value={s.cron_day_of_week || "*"} onValueChange={(v) => handleUpdateSchedule(s.job_id, "cron_day_of_week", v)} disabled={!isAdmin}>
                                  <SelectTrigger className="h-6 w-[120px] text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{DOW_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <span className="text-[10px] text-slate-400">{s.frequency_label} (Panama)</span>
                              </div>
                              {s.updated_by && <p className="text-[10px] text-slate-400 mt-1">Editado por {s.updated_by}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isAdmin && (
                              <Button variant="outline" size="sm" className="h-6 text-[11px] px-2"
                                onClick={(e) => { e.stopPropagation(); handleTrigger(s.job_id); }} disabled={triggeringJob === s.job_id}>
                                {triggeringJob === s.job_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3 mr-0.5" />}
                                Ejecutar
                              </Button>
                            )}
                            <Switch checked={s.enabled} onCheckedChange={(v) => handleToggle(s.job_id, v)}
                              disabled={!isAdmin || savingJob === s.job_id} className="scale-75" />
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Show alert rules for the alerts group */}
                    {group.id === "alerts" && alertRules.length > 0 && (
                      <div className="mt-2 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                        <p className="text-[11px] font-medium text-amber-700 mb-2">Alertas que se evaluaran ({alertRules.length}):</p>
                        <div className="max-h-[160px] overflow-y-auto space-y-1">
                          {alertRules.map(r => (
                            <div key={r.id} className="flex items-center gap-2 text-[11px] py-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                              <span className="text-slate-700 truncate flex-1">{r.name}</span>
                              <code className="text-[10px] bg-white px-1.5 py-0.5 rounded text-slate-500 flex-shrink-0">
                                {r.metric} {r.operator} {r.value}
                              </code>
                              <span className="text-[10px] text-slate-400 flex-shrink-0">{r.cliente || "Todos"}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {group.id === "alerts" && alertRules.length === 0 && (
                      <p className="text-[11px] text-slate-400 p-2">No hay alertas configuradas. Crea alertas desde el modulo Performance Alerts.</p>
                    )}
                    {/* Digest configuration */}
                    {group.id === "digest" && (
                      <div className="mt-2 p-3 rounded-lg bg-indigo-50/50 border border-indigo-100 space-y-3">
                        <div>
                          <p className="text-[11px] font-medium text-indigo-700 mb-1.5 flex items-center gap-1.5">
                            <Mail className="h-3 w-3" /> Configuracion del Digest
                          </p>
                          <p className="text-[10px] text-slate-500 mb-2">
                            El digest agrupa las cuentas en tres buckets: <b>sin gasto</b>, <b>bajo rendimiento</b> (caida de spend &gt; umbral) y <b>ok</b>. Se envia diariamente segun el horario configurado arriba.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Destinatarios (separados por coma)</label>
                            <Input
                              data-testid="digest-recipients-input"
                              value={digestDraft.recipients}
                              onChange={(e) => setDigestDraft(d => ({ ...d, recipients: e.target.value }))}
                              placeholder="analista@endavant.com, gerente@endavant.com"
                              disabled={!isAdmin}
                              className="h-7 text-[11px] mt-1"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Umbral bajo rend. (% caida)</label>
                            <Input
                              data-testid="digest-threshold-input"
                              type="number"
                              value={Math.abs(digestDraft.low_pct_threshold)}
                              onChange={(e) => setDigestDraft(d => ({ ...d, low_pct_threshold: -Math.abs(parseFloat(e.target.value) || 0) }))}
                              disabled={!isAdmin}
                              className="h-7 w-[90px] text-[11px]"
                              min="0" max="100" step="5"
                            />
                            <span className="text-[11px] text-slate-500">%</span>
                            <span className="text-[10px] text-slate-400 ml-1">
                              (una cuenta entra en bajo rend. si su spend cae ≥ {Math.abs(digestDraft.low_pct_threshold)}% vs dia anterior)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <div className="text-[10px] text-slate-400">
                            {digestSettings.updated_by && `Ultima edicion: ${digestSettings.updated_by}`}
                          </div>
                          {isAdmin && (
                            <div className="flex items-center gap-2">
                              <Button
                                data-testid="digest-test-send-btn"
                                variant="outline"
                                size="sm"
                                onClick={handleTestDigest}
                                disabled={testingDigest || savingDigest}
                                className="h-7 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                title="Envia el digest ahora SOLO a tu email (override de destinatarios)"
                              >
                                {testingDigest ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                                Enviar prueba ahora
                              </Button>
                              <Button
                                data-testid="digest-save-btn"
                                size="sm"
                                onClick={handleSaveDigest}
                                disabled={savingDigest}
                                className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                              >
                                {savingDigest ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                                Guardar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>

        {/* LOGS TAB */}
        <TabsContent value="logs" className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <Select value={logFilter} onValueChange={filterLogs}>
              <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue placeholder="Filtrar por job" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los jobs</SelectItem>
                {schedules.map(s => <SelectItem key={s.job_id} value={s.job_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">{logsTotal} ejecuciones</span>
          </div>

          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[22%]">Job</TableHead>
                  <TableHead className="w-[10%]">Estado</TableHead>
                  <TableHead className="w-[35%]">Mensaje</TableHead>
                  <TableHead className="w-[8%] text-right">Duracion</TableHead>
                  <TableHead className="w-[10%]">Origen</TableHead>
                  <TableHead className="w-[15%]">Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, i) => {
                  const st = STATUS_STYLES[log.status] || STATUS_STYLES.error;
                  const StIcon = st.icon;
                  const hasDetail = log.details?.rules_detail || log.details?.rules_checked;
                  const detail = log.details?.rules_detail || [];
                  const isExpLog = expandedLogs[i];
                  return (
                    <React.Fragment key={i}>
                    <TableRow className={`text-xs ${hasDetail ? "cursor-pointer hover:bg-slate-50" : ""}`}
                      onClick={() => hasDetail && setExpandedLogs(prev => ({ ...prev, [i]: !isExpLog }))}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge className={`text-[10px] ${JOB_ICONS[log.job_id] || "bg-slate-100"}`}>{log.job_id}</Badge>
                          {hasDetail && <ChevronRight className={`h-3 w-3 text-slate-300 transition-transform ${isExpLog ? "rotate-90" : ""}`} />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <StIcon className={`h-3.5 w-3.5 ${st.color} ${log.status === "started" || log.status === "running" ? "animate-spin" : ""}`} />
                          <span className={st.color}>{log.status}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 truncate max-w-0">{log.message}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500">{log.duration_seconds > 0 ? `${log.duration_seconds}s` : "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px]">{log.triggered_by === "scheduler" ? "Auto" : "Manual"}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 whitespace-nowrap">
                        {log.executed_at ? new Date(log.executed_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </TableCell>
                    </TableRow>
                    {isExpLog && detail.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-slate-50 p-0">
                          <div className="px-4 py-2 max-h-[200px] overflow-y-auto">
                            <p className="text-[10px] font-medium text-slate-500 mb-1.5">Alertas verificadas:</p>
                            <div className="space-y-1">
                              {detail.map((d, j) => (
                                <div key={j} className="flex items-center gap-2 text-[11px]">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${d.status === "triggered" ? "bg-amber-500" : "bg-emerald-400"}`} />
                                  <span className="text-slate-700 flex-1 truncate">{d.rule_name}</span>
                                  <code className="text-[10px] bg-white px-1.5 py-0.5 rounded border text-slate-500">{d.condition}</code>
                                  <span className="text-[10px] text-slate-400">{d.cliente}</span>
                                  <span className={`text-[10px] font-medium ${d.status === "triggered" ? "text-amber-600" : "text-emerald-600"}`}>
                                    {d.status === "triggered" ? `${d.accounts_triggered} cuentas` : "OK"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
                {logs.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No hay ejecuciones registradas</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {logsHasMore && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={loadMoreLogs} disabled={loadingMore} className="text-xs">
                {loadingMore ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Cargando...</> : "Cargar mas"}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
